import { useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Printer, Bluetooth, Usb, Scissors, CheckCircle2, Cable } from "lucide-react";
import { CopyCodeButton } from "@/components/CopyCode";
import { DesktopPrinterPanel } from "@/components/pos/DesktopPrinterPanel";
import { isDesktopApp, isDesktopRawActive, printTicketViaDesktop, systemPrint } from "@/lib/desktopPrint";
import { toast } from "sonner";
import { getPrinterSettings, savePrinterSettings } from "@/lib/printerConfig";
import {
  isWebBluetoothAvailable,
  pairPrinter,
  getSavedPrinter,
  forgetPrinter,
  printTicketViaBluetooth,
} from "@/lib/bluetoothPrint";
import { isWebSerialAvailable, pairSerialPrinter, getSavedSerialPrinter, forgetSerialPrinter, printTicketViaSerial, SERIAL_BAUD_RATES } from "@/lib/serialPrint";

const IS_MAC = typeof navigator !== "undefined" && /Mac/i.test(navigator.platform || navigator.userAgent);
const kioskCommand = (url) => (IS_MAC
  ? `open -na "Google Chrome" --args --kiosk-printing --app=${url}`
  : `"C:\\Program Files\\Google\\Chrome\\Application\\chrome.exe" --kiosk-printing --app=${url}`);

const DEMO_TICKET = {
  code: "DEMO-TEST-001",
  created_at: new Date().toISOString(),
  plays: [
    { lottery_type_id: "demo", sorteo_id: "demo", number: "123", sign: null, amount_bs: 10 },
    { lottery_type_id: "demo2", sorteo_id: "demo", number: "77", sign: "Leo", amount_bs: 5 },
  ],
  total_bs: 15,
  total_usd: 0.3,
  exchange_rate: 50,
};
const DEMO_LOTTERY_MAP = { demo: { name: "Triple Prueba" }, demo2: { name: "Zodiacal Prueba" } };
const DEMO_SORTEO_MAP = { demo: { scheduled_at: new Date().toISOString(), label: "Prueba" } };

export function PrinterSettingsDialog({ open, onClose, operatorName = "" }) {
  const [settings, setSettings] = useState(getPrinterSettings());
  const [saved, setSaved] = useState(getSavedPrinter());
  const [busy, setBusy] = useState(false);
  const [serialSaved, setSerialSaved] = useState(getSavedSerialPrinter());
  const serialAvailable = isWebSerialAvailable() && !window.electronAPI?.isElectron;

  const btAvailable = typeof window !== "undefined" && isWebBluetoothAvailable() && !window.electronAPI?.isElectron;
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const demoUrl = `${origin}/validar/DEMO-TEST-001`;

  const update = (patch) => {
    const next = savePrinterSettings({ ...settings, ...patch });
    setSettings(next);
  };

  const doPair = async () => {
    if (!btAvailable) { toast.error("Web Bluetooth no está disponible en este navegador."); return; }
    setBusy(true);
    const tId = toast.loading("Selecciona tu impresora Bluetooth…");
    try {
      const res = await pairPrinter();
      setSaved(getSavedPrinter());
      toast.success(`Emparejada: ${res.device?.name || "impresora"}`);
    } catch (e) {
      if (!/cancel|NotFoundError|User cancelled/i.test(e?.message || "")) {
        toast.error("Bluetooth: " + (e?.message || "error de emparejamiento"));
      }
    } finally {
      toast.dismiss(tId);
      setBusy(false);
    }
  };

  const doSerialPair = async () => {
    if (!serialAvailable) { toast.error("Web Serial no está disponible (usa Chrome o Edge de escritorio)."); return; }
    setBusy(true);
    try {
      const { info } = await pairSerialPrinter(settings.serial_baud);
      setSerialSaved(info);
      toast.success(`Impresora directa vinculada: ${info.name}`);
    } catch (e) {
      if (!/cancel|NotFoundError|No port selected/i.test(e?.message || "")) toast.error("Impresora directa: " + (e?.message || "error al vincular"));
    } finally {
      setBusy(false);
    }
  };
  const doSerialForget = async () => { await forgetSerialPrinter(); setSerialSaved(null); toast.message("Impresora directa olvidada"); };

  const doForget = () => { forgetPrinter(); setSaved(null); toast.message("Impresora Bluetooth olvidada"); };

  const doTestPrint = async () => {
    if (isDesktopRawActive()) {
      setBusy(true);
      const res = await printTicketViaDesktop(DEMO_TICKET, {
        lotteryMap: DEMO_LOTTERY_MAP, sorteoMap: DEMO_SORTEO_MAP, operatorName, validationUrl: demoUrl,
        paperSize: settings.paper_size, feedLines: settings.feed_lines, cutPaper: settings.cut_paper,
      });
      setBusy(false);
      if (res.ok) toast.success(`Prueba enviada a ${res.device?.name || "impresora"}`);
      else toast.error("Impresión RAW: " + res.error);
      return;
    }
    if (settings.print_method === "serial" && !isDesktopApp()) {
      setBusy(true);
      const res = await printTicketViaSerial(DEMO_TICKET, {
        lotteryMap: DEMO_LOTTERY_MAP, sorteoMap: DEMO_SORTEO_MAP, operatorName, validationUrl: demoUrl,
        paperSize: settings.paper_size, feedLines: settings.feed_lines, cutPaper: settings.cut_paper, baudRate: settings.serial_baud,
      });
      setBusy(false);
      setSerialSaved(getSavedSerialPrinter());
      if (res.ok) toast.success(`Prueba enviada a ${res.device?.name || "impresora directa"}`);
      else if (!/cancel|NotFoundError|No port selected/i.test(res.error || "")) toast.error("Impresora directa: " + res.error);
      return;
    }
    if (settings.print_method === "bluetooth" && !isDesktopApp()) {
      if (!btAvailable) { toast.error("Web Bluetooth no está disponible en este navegador."); return; }
      setBusy(true);
      const tId = toast.loading("Enviando ticket de prueba…");
      const res = await printTicketViaBluetooth(DEMO_TICKET, {
        lotteryMap: DEMO_LOTTERY_MAP, sorteoMap: DEMO_SORTEO_MAP, operatorName,
        validationUrl: demoUrl, paperSize: settings.paper_size,
        feedLines: settings.feed_lines, cutPaper: settings.cut_paper,
      });
      setBusy(false);
      toast.dismiss(tId);
      if (res.ok) toast.success(`Prueba enviada a ${res.device?.name || "impresora"}`);
      else if (!/cancel|NotFoundError|User cancelled/i.test(res.error || "")) toast.error("Bluetooth: " + res.error);
      return;
    }
    // Método navegador/USB: el área #printer-test-print-area es la única visible al imprimir.
    systemPrint();
  };

  const paperClass = settings.paper_size === "80mm" ? "thermal-80" : "thermal-58";

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="bg-card border-border max-w-lg max-h-[90vh] overflow-y-auto" data-testid="printer-settings-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Printer size={18} className="text-primary"/> Configuración de impresora</DialogTitle>
          <DialogDescription>Ajustes de tiquera guardados en este equipo (por taquilla).</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 no-print">
          {/* Ancho de papel */}
          <div className="space-y-2">
            <Label>Ancho de papel</Label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => update({ paper_size: "58mm" })}
                data-testid="printer-paper-58"
                className={`rounded-lg border-2 p-3 text-left transition-colors ${settings.paper_size === "58mm" ? "border-primary bg-primary/10" : "border-border hover:border-primary/40"}`}
              >
                <div className="font-semibold text-sm">58 mm</div>
                <div className="text-[11px] text-muted-foreground">Recibo estrecho (agencia estándar)</div>
              </button>
              <button
                type="button"
                onClick={() => update({ paper_size: "80mm" })}
                data-testid="printer-paper-80"
                className={`rounded-lg border-2 p-3 text-left transition-colors ${settings.paper_size === "80mm" ? "border-primary bg-primary/10" : "border-border hover:border-primary/40"}`}
              >
                <div className="font-semibold text-sm">80 mm</div>
                <div className="text-[11px] text-muted-foreground">Recibo ancho</div>
              </button>
            </div>
          </div>

          {isDesktopApp() && <DesktopPrinterPanel settings={settings} update={update} />}

          {/* Método de impresión */}
          <div className={`space-y-2 ${isDesktopApp() ? "hidden" : ""}`}>
            <Label>Método de impresión</Label>
            <div className="grid grid-cols-1 gap-2">
              <button
                type="button"
                onClick={() => update({ print_method: "browser" })}
                data-testid="printer-method-browser"
                className={`flex items-center gap-3 rounded-lg border-2 p-3 text-left transition-colors ${settings.print_method === "browser" ? "border-primary bg-primary/10" : "border-border hover:border-primary/40"}`}
              >
                <Usb size={18} className="text-primary shrink-0"/>
                <div>
                  <div className="font-semibold text-sm">Impresora del Sistema / USB</div>
                  <div className="text-[11px] text-muted-foreground">Usa el spooler del sistema operativo.</div>
                </div>
              </button>
              <button
                type="button"
                onClick={() => { if (serialAvailable) update({ print_method: "serial" }); }}
                disabled={!serialAvailable}
                data-testid="printer-method-serial"
                className={`flex items-center gap-3 rounded-lg border-2 p-3 text-left transition-colors ${!serialAvailable ? "opacity-50 cursor-not-allowed border-border" : settings.print_method === "serial" ? "border-primary bg-primary/10" : "border-border hover:border-primary/40"}`}
              >
                <Cable size={18} className="text-primary shrink-0"/>
                <div className="min-w-0">
                  <div className="font-semibold text-sm">Impresora directa · USB / Bluetooth clásico (sin diálogo)</div>
                  <div className="text-[11px] text-muted-foreground truncate" data-testid="printer-serial-status">
                    {!serialAvailable ? "No disponible en este navegador (usa Chrome/Edge de escritorio)." : serialSaved ? `Vinculada: ${serialSaved.name}` : "ESC/POS directo por puerto serie · sin vincular."}
                  </div>
                </div>
              </button>
              <button
                type="button"
                onClick={() => { if (btAvailable) update({ print_method: "bluetooth" }); }}
                disabled={!btAvailable}
                data-testid="printer-method-bluetooth"
                className={`flex items-center gap-3 rounded-lg border-2 p-3 text-left transition-colors ${!btAvailable ? "opacity-50 cursor-not-allowed border-border" : settings.print_method === "bluetooth" ? "border-primary bg-primary/10" : "border-border hover:border-primary/40"}`}
              >
                <Bluetooth size={18} className="text-primary shrink-0"/>
                <div className="min-w-0">
                  <div className="font-semibold text-sm">Impresora Térmica Bluetooth (ESC/POS)</div>
                  <div className="text-[11px] text-muted-foreground truncate">
                    {!btAvailable ? "No disponible en este navegador (usa Chrome/Edge Android)." : saved ? `Emparejada: ${saved.name}` : "Sin emparejar."}
                  </div>
                </div>
              </button>
            </div>
            {settings.print_method === "bluetooth" && btAvailable && (
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={doPair} disabled={busy} data-testid="printer-bt-pair">
                  <Bluetooth size={14} className="mr-1.5"/>{saved ? "Reemparejar" : "Emparejar impresora"}
                </Button>
                {saved && <Button variant="ghost" size="sm" className="text-muted-foreground text-xs" onClick={doForget} data-testid="printer-bt-forget">Olvidar</Button>}
              </div>
            )}
            {settings.print_method === "bluetooth" && btAvailable && (
              <div className="text-[11px] text-muted-foreground leading-snug rounded-md bg-secondary/40 p-2" data-testid="printer-bt-help">
                ¿Se queda en "Buscando…"? Este modo solo detecta impresoras Bluetooth LE. La mayoría de las térmicas 58mm son Bluetooth clásico: emparéjala primero en los ajustes Bluetooth del sistema y usa <b>Impresora directa</b>.
                {IS_MAC ? " En Mac, permite Bluetooth a Chrome en Ajustes del Sistema → Privacidad y seguridad → Bluetooth." : ""}
              </div>
            )}
            {settings.print_method === "serial" && serialAvailable && (
              <div className="space-y-2" data-testid="printer-serial-panel">
                <div className="flex items-center gap-2 flex-wrap">
                  <Button variant="outline" size="sm" onClick={doSerialPair} disabled={busy} data-testid="printer-serial-pair">
                    <Cable size={14} className="mr-1.5"/>{serialSaved ? "Cambiar puerto" : "Vincular puerto"}
                  </Button>
                  {serialSaved && <Button variant="ghost" size="sm" className="text-muted-foreground text-xs" onClick={doSerialForget} data-testid="printer-serial-forget">Olvidar</Button>}
                  <Label className="text-xs ml-auto">Baudios</Label>
                  <select value={settings.serial_baud} onChange={(e) => update({ serial_baud: e.target.value })} data-testid="printer-serial-baud"
                    className="h-8 rounded-md border border-border bg-background px-2 text-xs">
                    {SERIAL_BAUD_RATES.map((b) => <option key={b} value={b}>{b}</option>)}
                  </select>
                </div>
                <div className="text-[11px] text-muted-foreground leading-snug">
                  Selecciona el puerto COM/USB de la tiquera o su Bluetooth (emparejado antes en el sistema). Se autoriza una sola vez; luego imprime directo, también al emitir. Si salen caracteres extraños, prueba 19200 o 115200 baudios.
                </div>
              </div>
            )}
          </div>

          {/* Auto print */}
          <div className="flex items-center justify-between rounded-lg border border-border p-3">
            <div>
              <Label htmlFor="auto-print" className="text-sm">Imprimir automáticamente al emitir ticket</Label>
              <div className="text-[11px] text-muted-foreground">Al confirmar una venta, imprime sin pasos extra.</div>
            </div>
            <Switch id="auto-print" checked={settings.auto_print_on_sell} onCheckedChange={(v) => update({ auto_print_on_sell: v })} data-testid="printer-auto-print-switch"/>
          </div>

          {/* Terminal kiosco (Chromium --kiosk-printing) */}
          <div className={`rounded-lg border border-border p-3 space-y-1.5 ${isDesktopApp() ? "hidden" : ""}`}>
            <div className="flex items-center justify-between">
              <Label htmlFor="kiosk-print" className="text-sm">Terminal kiosco (impresión silenciosa)</Label>
              <Switch id="kiosk-print" checked={settings.kiosk_printing} onCheckedChange={(v) => update({ kiosk_printing: v })} data-testid="printer-kiosk-switch"/>
            </div>
            <div className="text-[11px] text-muted-foreground leading-snug" data-testid="printer-kiosk-help">
              Ejecuta Chrome/Edge con <code className="font-mono text-foreground">--kiosk-printing</code> para enviar el ticket a la impresora predeterminada sin el cuadro de diálogo. Con esta opción activa el recibo se cierra solo tras imprimir.
            </div>
            <div className="text-[11px] leading-snug rounded-md bg-secondary/40 p-2 space-y-1" data-testid="printer-kiosk-guide">
              <div className="font-semibold">Acceso directo ({IS_MAC ? "Mac" : "Windows"}) para impresoras con driver del sistema:</div>
              <ol className="list-decimal ml-4 text-muted-foreground space-y-0.5">
                <li>Define la tiquera como impresora predeterminada del sistema.</li>
                <li>{IS_MAC ? "Cierra Chrome por completo (Cmd+Q) y ejecuta en Terminal:" : "Clic derecho en el escritorio → Nuevo → Acceso directo, pega en Ubicación:"}</li>
              </ol>
              <div className="flex items-start gap-1">
                <code className="font-mono text-[10px] break-all flex-1 bg-background/60 rounded p-1.5" data-testid="printer-kiosk-command">{kioskCommand(`${origin}/pos`)}</code>
                <CopyCodeButton code={kioskCommand(`${origin}/pos`)} label="Comando" testId="printer-kiosk-command-copy" />
              </div>
              <div className="text-muted-foreground">{IS_MAC ? "" : "Cierra todas las ventanas de Chrome y abre el POS siempre desde ese acceso directo. "}Luego activa este interruptor.</div>
            </div>
          </div>

          {/* Corte + feed */}
          <div className="grid grid-cols-2 gap-3">
            <div className="flex items-center justify-between rounded-lg border border-border p-3">
              <Label htmlFor="cut-paper" className="text-sm flex items-center gap-1.5"><Scissors size={14}/> Cortar papel</Label>
              <Switch id="cut-paper" checked={settings.cut_paper} onCheckedChange={(v) => update({ cut_paper: v })} data-testid="printer-cut-switch"/>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Saltos de línea finales (mín. 4 en Bluetooth)</Label>
              <Input type="number" min="0" max="10" value={settings.feed_lines} onChange={(e) => update({ feed_lines: parseInt(e.target.value, 10) || 0 })} data-testid="printer-feed-lines"/>
            </div>
          </div>
        </div>

        <DialogFooter className="no-print">
          <Button variant="outline" onClick={doTestPrint} disabled={busy} data-testid="printer-test-print">
            <Printer size={14} className="mr-1.5"/>Imprimir Ticket de Prueba
          </Button>
          <Button onClick={onClose} data-testid="printer-settings-close"><CheckCircle2 size={14} className="mr-1.5"/>Listo</Button>
        </DialogFooter>

        {/* Área de prueba imprimible (solo visible al imprimir vía navegador) */}
        <div className="printer-offscreen">
          <div id="printer-test-print-area" className={`thermal-ticket ${paperClass}`}>
            <div className="text-center">
              <div className="font-bold tracking-[0.32em] text-[15px] leading-tight">CONNECT</div>
              <div className="text-[9px] tracking-[0.2em] leading-tight">SISTEMA DE JUEGOS</div>
              {operatorName ? <div className="text-[10px] mt-1.5">{operatorName}</div> : null}
              <div className="text-[10px] font-bold tracking-[0.15em] mt-1">*** TICKET DE PRUEBA ***</div>
              <div className="text-[10px] mt-1 th-mono">{new Date().toLocaleString("es-VE")}</div>
            </div>
            <div className="th-sep-solid" />
            <div className="text-center">
              <div className="text-[9px] tracking-[0.2em]">SERIAL</div>
              <div className="text-[15px] font-bold tracking-[0.18em] th-mono leading-tight mt-0.5">DEMO-TEST-001</div>
            </div>
            <div className="th-sep" />
            <div className="th-row text-[13px] font-bold"><span>TOTAL Bs</span><span className="th-mono">15,00</span></div>
            <div className="th-sep" />
            <div className="flex flex-col items-center gap-1">
              <QRCodeCanvas value={demoUrl} size={settings.paper_size === "80mm" ? 120 : 96} level="M" includeMargin={true} bgColor="#ffffff" fgColor="#000000" />
              <div className="text-[8px] text-center break-all leading-tight opacity-80">{demoUrl}</div>
            </div>
            <div className="th-sep" />
            <div className="text-center text-[8.5px] leading-snug">Prueba de impresora Connect.<br/>Ancho {settings.paper_size} · corte {settings.cut_paper ? "ON" : "OFF"}.</div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default PrinterSettingsDialog;
